import KMLGenerator from "@/components/KMLGenerator";
import KMLViewer from "@/components/KMLViewer";
import EmbeddedKMLGenerator from "@/components/EmbeddedKMLGenerator";
import FAQ from "@/components/FAQ";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { ExternalLink, MapPin, Download, Globe } from "lucide-react";
import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
const Index = () => {
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [mode, setMode] = useState<'generator' | 'viewer'>('generator');
  const {
    language,
    t
  } = useLanguage();
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setIsEmbedded(urlParams.get('embed') === 'true');
  }, []);
  const generateEmbedLink = () => {
    const embedUrl = `${window.location.origin}?embed=true&lang=${language}`;

    // Copy to clipboard
    navigator.clipboard.writeText(embedUrl).then(() => {
      // Create a temporary notification
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #10b981;
        color: white;
        padding: 12px 16px;
        border-radius: 6px;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        font-size: 14px;
        font-weight: 500;
      `;
      notification.textContent = t('actions.embedCopied');
      document.body.appendChild(notification);
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 3000);
    });
  };

  // Show embedded version if embed parameter is present
  if (isEmbedded) {
    return <EmbeddedKMLGenerator />;
  }

  // Show full page version
  return <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 px-0 py-[31px]">
      <div className="container mx-0 my-0">
        <div className="flex justify-end mb-4">
          <LanguageToggle />
        </div>
        
        <main>
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('main.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-6">
            {t('main.description')}
          </p>
          
          <Button onClick={generateEmbedLink} variant="outline" className="flex items-center gap-2 mx-auto">
            <ExternalLink className="h-4 w-4" />
            {t('actions.getEmbed')}
          </Button>
        </div>
        
        {mode === 'generator' ? <KMLGenerator mode={mode} onModeChange={setMode} /> : <KMLViewer mode={mode} onModeChange={setMode} />}

         
        
        {/* SEO Content Section */}
        <div className="mt-16 max-w-4xl mx-auto">

          {/* Key Features Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <MapPin className="h-8 w-8 text-blue-600 mx-auto mb-3" />
              <h2 className="font-semibold text-gray-900 mb-2 text-lg">Interactive Drawing</h2>
              <p className="text-sm text-gray-600">Draw precise polygons and areas directly on the map with our intuitive click-to-draw interface.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <Download className="h-8 w-8 text-blue-600 mx-auto mb-3" />
              <h2 className="font-semibold text-gray-900 mb-2 text-lg">Instant KML Export</h2>
              <p className="text-sm text-gray-600">Download your drawn areas as KML files ready for Google Earth, GPS devices, and mapping software.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <Globe className="h-8 w-8 text-blue-600 mx-auto mb-3" />
              <h2 className="font-semibold text-gray-900 mb-2 text-lg">Universal Compatibility</h2>
              <p className="text-sm text-gray-600">Works with Google Earth, Google Maps, and all major mapping applications that support KML format.</p>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Professional KML File Generator for Google Earth & Mapping Applications
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="mb-4">
                Our free online KML generator is the perfect tool for creating geographic data files compatible with Google Earth, 
                GPS devices, and professional mapping software. Whether you're a surveyor, researcher, real estate professional, 
                or outdoor enthusiast, our intuitive map-based drawing tool makes it easy to create precise KML files.
              </p>
              <p className="mb-4">
                KML (Keyhole Markup Language) files are the standard format for sharing geographic information. With our generator, 
                you can draw polygons directly on an interactive map, calculate accurate area measurements, and export your data 
                as professional-grade KML files ready for use in any compatible application.
              </p>
              <p>
                The tool includes advanced features like address search for quick navigation, real-time area calculations, 
                and the ability to create multiple areas in a single KML file. Best of all, it's completely free with no 
                registration required and no limitations on usage.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <FAQ />
        
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">{t('footer.perfectFor')}</p>
        </div>
        </main>
      </div>
    </div>;
};
export default Index;